import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import ReactSelect from "react-select";
// FIREBASE
import { useFirestore } from "../../firebase-files/hooks/useFirestore";
import { useDocument } from "../../firebase-files/hooks/useDocument";
import { useCollection } from "../../firebase-files/hooks/useCollection";
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

const EditProductDetails = ({ open, onClose, field }) => {
  const { id } = useParams();
  const { updateDocument } = useFirestore("products");
  const { document: productDoc } = useDocument("products", id);
  const { documents: vendors } = useCollection("vendors");
  // Local state
  const [value, setValue] = useState("");
  const [discount, setDiscount] = useState("");
  const [gift, setGift] = useState("");
  const [rating, setRating] = useState("");
  const [isGiftAvailable, setIsGiftAvailable] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);

  // Convert vendors to react-select options
  const vendorOptions = vendors
    ? [
        { value: "all", label: "All Vendors" }, // ⭐ Add this at top
        ...vendors.map((v) => ({
          value: v.id,
          label: v.name,
        })),
      ]
    : [];

  const handleVendorSelection = (selectedOptions) => {
    // If no selections
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedVendors([]);
      return;
    }

    // Check if "All Vendors" is selected
    const isAllSelected = selectedOptions.some((opt) => opt.value === "all");

    if (isAllSelected) {
      // Keep ONLY "all"
      setSelectedVendors([{ value: "all", label: "All Vendors" }]);
    } else {
      // Normal multi-selection without "all"
      setSelectedVendors(selectedOptions);
    }
  };

  // Pre-fill existing values
  useEffect(() => {
    if (open && productDoc) {
      if (field === "price_discount_gift") {
        setValue(productDoc.price?.toString() || "");
        setDiscount(productDoc.discount?.toString() || "");

        const giftPrice = productDoc.giftWrapPrice;
        setGift(
          giftPrice !== null && giftPrice !== undefined
            ? giftPrice.toString()
            : ""
        );

        // ✅ Checkbox logic
        setIsGiftAvailable(giftPrice !== null && giftPrice !== undefined);
      } else {
        switch (field) {
          case "name":
            setValue(productDoc.name || "");
            break;
          case "vendorDetails":
            setValue(productDoc.vendorDetails || "");
            break;
          case "price":
            setValue(productDoc.price?.toString() || "");
            break;
          case "rating":
            setRating(productDoc.rating?.toString() || "");
            break;
          case "short_description":
            setValue(productDoc.short_description || "");
            break;
          case "description":
            setValue(productDoc.description || "");
            break;
          case "specifications":
            setValue(productDoc.specifications || "");
            break;
          default:
            setValue("");
        }
      }
    }
  }, [open, productDoc, field]);

  // Save handler
  const handleSave = async () => {
    try {
      if (field === "price_discount_gift") {
        const updateData = {
          price: value ? parseFloat(value) : 0,
          discount: discount ? parseFloat(discount) : 0,
        };

        if (isGiftAvailable && gift) {
          updateData.giftWrapPrice = parseFloat(gift);
          updateData.giftAllow = true;
        } else {
          updateData.giftWrapPrice = null;
          updateData.giftAllow = false;
        }

        await updateDocument(id, updateData);
      } else {
        switch (field) {
          case "name":
            await updateDocument(id, { name: value });
            break;
          case "vendorLocation":
            const vendorIdsArray = selectedVendors.map((v) => v.value);
            await updateDocument(id, { showProductTo: vendorIdsArray });
            break;
          case "price":
            await updateDocument(id, { price: parseFloat(value) });
            break;
          case "rating":
            await updateDocument(id, { rating: parseFloat(rating) });
            break;
          case "short_description":
            await updateDocument(id, { short_description: value });
            break;
          case "description":
            await updateDocument(id, { description: value });
            break;
          case "specifications":
            await updateDocument(id, { specifications: value });
            break;
          default:
            console.warn("Unknown field:", field);
            break;
        }
      }
      onClose();
    } catch (error) {
      console.error("Error updating field:", error);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="edit-product-title">
      <Box sx={modalStyle}>
        <h2 id="edit-product-title">
          {field === "price_discount_gift"
            ? "Edit Price, Discount & Gift"
            : field === "short_description"
            ? "Edit Short Description"
            : field === "vendorLocation"
            ? "Vendor Location"
            : `Edit ${field}`}
        </h2>

        {field === "price_discount_gift" ? (
          <>
            <TextField
              variant="outlined"
              type="number"
              label="Product Price"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              fullWidth
              margin="normal"
            />

            <TextField
              variant="outlined"
              type="number"
              label="Product Discount (%)"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              fullWidth
              margin="normal"
            />

            {/* ✅ GIFT CHECKBOX */}
            <div className="d-flex align-items-center mt-2">
              <input
                type="checkbox"
                id="giftAvailable"
                checked={isGiftAvailable}
                onChange={(e) => setIsGiftAvailable(e.target.checked)}
              />
              <label htmlFor="giftAvailable" style={{ marginLeft: "8px" }}>
                Gift Wrap Available
              </label>
            </div>

            {/* ✅ SHOW INPUT & Free Button WHEN CHECKED */}
            {isGiftAvailable && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <Button
                  variant={gift === "0" ? "contained" : "outlined"}
                  color="success"
                  onClick={() => setGift("0")}
                  style={{ marginRight: "10px", minWidth: "90px" }}
                >
                  Free
                </Button>

                <TextField
                  variant="outlined"
                  type="number"
                  label="Enter Gift Wrap Price"
                  value={gift}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== "0") setGift(val);
                    else setGift("0");
                  }}
                  onFocus={() => {
                    if (gift === "0") setGift("");
                  }}
                />
              </div>
            )}
          </>
        ) : field === "rating" ? (
          <FormControl fullWidth margin="normal">
            <InputLabel>Rating</InputLabel>
            <Select
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              label="Rating"
            >
              {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((val) => (
                <MenuItem key={val} value={val}>
                  {val}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : field === "vendorLocation" ? (
          <ReactSelect // 👈 Changed from Select to ReactSelect
            isMulti
            options={vendorOptions}
            value={selectedVendors}
            onChange={handleVendorSelection}
            className="vendor-multiselect"
            classNamePrefix="vendor-select"
            placeholder="Select vendors..."
            // 👇 IMPORTANT: This allows the menu to overflow on top of the Modal
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              // Optional: Fix text color if MUI styles interfere
              option: (base) => ({ ...base, color: "black" }),
            }}
          />
        ) : (
          <TextField
            variant="outlined"
            type={field === "price" || field === "stock" ? "number" : "text"}
            label={field}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            fullWidth
            margin="normal"
          />
        )}

        <div
          className="d-flex align-items-center justify-content-between"
          style={{ marginTop: "16px" }}
        >
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              field === "price_discount_gift"
                ? !value
                : field === "rating"
                ? !rating
                : field === "vendorLocation"
                ? selectedVendors.length === 0 // ⭐ Enable when user selects any location
                : !value
            }
            style={{ marginLeft: "8px" }}
          >
            Save
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default EditProductDetails;
